import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ShieldCheck, Users, Award, Clock } from "lucide-react";
import { FadeIn } from "@/hooks/useFadeIn";
import { SiteImage } from "@/components/SiteImage";
import axios from "axios";
import SEO from "@/components/SEO";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AboutPage = () => {
  const [aboutImage, setAboutImage] = useState(null);

  useEffect(() => {
    const fetchImage = async () => {
      try {
        const res = await axios.get(`${API}/galleries/om-os`);
        const images = res.data?.images || [];
        if (images.length > 0) {
          setAboutImage(images[0].url);
        }
      } catch (err) {
        console.error("Error fetching about image:", err);
      }
    };
    fetchImage();
  }, []);

  const values = [
    {
      icon: ShieldCheck,
      title: "Kvalitet",
      description: "Vi går aldrig på kompromis med kvaliteten. Hvert projekt udføres med omhu og faglig stolthed.",
    },
    {
      icon: Clock,
      title: "Pålidelighed",
      description: "Vi holder hvad vi lover. Aftaler overholdes, og vi møder til tiden.",
    },
    {
      icon: Users,
      title: "Kommunikation",
      description: "Klar og ærlig kommunikation gennem hele projektet. Ingen overraskelser.",
    },
    {
      icon: Award,
      title: "Erfaring",
      description: "Mange års erfaring med anlægsarbejde i København og omegn.",
    },
  ];

  return (
    <div data-testid="about-page" className="pt-20">
      <SEO 
        title="Om os | Birk Anlæg"
        description="Birk Anlæg er en professionel anlægsgartnervirksomhed med over 20 års erfaring i København og omegn. Vi fokuserer på kvalitet, pålidelighed og solidt håndværk."
        canonical="/om-os"
      />
      {/* Hero */}
      <section className="bg-[#1A3C34] py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1
            data-testid="about-title"
            className="text-4xl md:text-6xl font-bold text-white mb-6"
            style={{ fontFamily: "Manrope, sans-serif" }}
          >
            Om Birk Anlæg
          </h1>
          <p className="text-xl text-gray-300 max-w-2xl mx-auto">
            Professionel anlægsgartner med fokus på kvalitet, pålidelighed 
            og håndværk i højsædet.
          </p>
        </div>
      </section>

      {/* About Content */}
      <FadeIn>
        <section className="py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            {/* Image */}
            <div className="relative mx-8 sm:mx-12 lg:mx-0">
              <SiteImage
                src={aboutImage}
                alt="Birk Anlæg arbejde"
                aspectRatio="4/3"
                containerClassName="rounded-lg"
              />
              <div className="absolute -bottom-4 -right-4 sm:-bottom-6 sm:-right-6 bg-[#1A3C34] text-white p-4 sm:p-6 rounded-lg shadow-lg">
                <p className="text-2xl sm:text-4xl font-bold" style={{ fontFamily: "Manrope, sans-serif" }}>20+</p>
                <p className="text-xs sm:text-sm">Års erfaring</p>
              </div>
            </div>

            {/* Content */}
            <div>
              <span className="text-[#1A3C34] font-medium text-sm uppercase tracking-wider">
                Hvem er vi
              </span>
              <h2
                className="text-3xl md:text-4xl font-semibold text-[#1A3C34] mt-2 mb-6"
                style={{ fontFamily: "Manrope, sans-serif" }}
              >
                Håndværk med passion
              </h2>
              <div className="space-y-4 text-gray-600 text-lg leading-relaxed">
                <p>
                  Birk Anlæg er en anlægsgartnervirksomhed med base i København. Vi udfører flisebelægning, rullegræs, hegn, natursten og anlægsopgaver for både private, virksomheder og boligforeninger.
                </p>
                <p>
                  Vi har mange års erfaring fra branchen og arbejder med klare aftaler og en grundig tilgang til opgaverne.
                  Arbejdet planlægges med udgangspunkt i de konkrete forhold.
                </p>
                <p>
                  Vi arbejder med gennemprøvede metoder og materialer, der er valgt med henblik på holdbarhed og funktion. Målet er løsninger, der fungerer i praksis og holder over tid.
                </p>
              </div>
              <Link
                to="/kontakt"
                data-testid="about-cta"
                data-cta="primary"
                data-cta-variant="green"
                className="inline-flex items-center gap-2 bg-[#1A3C34] text-white rounded-full px-8 py-4 font-medium mt-8"
              >
                Kontakt os <ArrowRight size={18} />
              </Link>
            </div>
          </div>
        </div>
      </section>
      </FadeIn>

      {/* Values */}
      <FadeIn>
        <section className="bg-[#F9FAFB] py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2
              className="text-3xl md:text-4xl font-semibold text-[#1A3C34] mb-4"
              style={{ fontFamily: "Manrope, sans-serif" }}
            >
              Vores værdier
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Det vi står for og det vores kunder kan forvente af os.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <div
                key={index}
                data-testid={`value-card-${index}`}
                className="bg-white p-8 rounded-2xl hover:shadow-lg transition-shadow"
              >
                <value.icon size={40} className="text-[#1A3C34] mb-4" />
                <h3
                  className="text-xl font-semibold text-[#1A3C34] mb-3"
                  style={{ fontFamily: "Manrope, sans-serif" }}
                >
                  {value.title}
                </h3>
                <p className="text-gray-600">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
      </FadeIn>

      {/* Dækningsområde - Clean text-based transition section */}
      <section className="py-28 md:py-36 bg-[#F7FAF9] relative">
        {/* Subtle top divider */}
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#1A3C34]/10 to-transparent" />
        
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2
            className="text-3xl md:text-4xl font-bold text-[#1A3C34] mb-10"
            style={{ fontFamily: "Manrope, sans-serif" }}
          >
            Dækningsområde
          </h2>
          <div className="space-y-6 text-gray-600 text-lg leading-relaxed">
            <p>
              Vores primære arbejdsområde er Storkøbenhavn og omegn, men vi løser også 
              opgaver på resten af Sjælland. Ved større eller særligt interessante opgaver 
              påtager vi os indimellem arbejde på Fyn og i Jylland.
            </p>
            <p>
              Vi vælger vores opgaver med fokus på kvalitet, holdbarhed og et gennemarbejdet resultat.
            </p>
          </div>
        </div>
      </section>

      {/* CTA */}
      <FadeIn>
        <section className="bg-[#1A3C34] py-20">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2
              className="text-3xl md:text-4xl font-semibold text-white mb-6"
              style={{ fontFamily: "Manrope, sans-serif" }}
            >
              Klar til at komme i gang?
            </h2>
            <p className="text-xl text-gray-300 mb-10">
              Kontakt os i dag for en uforpligtende snak om dit projekt.
            </p>
            <Link
              to="/kontakt"
              data-testid="about-bottom-cta"
              data-cta="primary"
              data-cta-variant="white"
              className="inline-flex items-center gap-2 bg-white text-[#1A3C34] rounded-full px-10 py-4 text-lg font-medium"
            >
              Kontakt os <ArrowRight size={20} />
            </Link>
          </div>
        </section>
      </FadeIn>
    </div>
  );
};

export default AboutPage;
